/**
 * R2 (SPEC-bc-pst-tax-engine-2026-08-04): shared tax-class vocabulary.
 *
 * `tax_class` is billable_items.tax_class (V218) — the service class the
 * rules-mode engine keys rule selection on (services/tax_engine.py
 * TAX_CLASS_TAXONOMY). It is deliberately SEPARATE from billable item
 * `category`, which conflates taxability with revenue-vs-expense.
 *
 * The class list mirrors the backend taxonomy exactly; the labels are what
 * Christa sees on the invoice line (AC30) and in the category settings
 * (AC28). Keep in sync with services/tax_engine.py.
 *
 * v0.17 taxclass-light (2026-09-14): every dropdown shows the class NAME
 * only -- the QuickBooks/Xero convention of "short at the line, detail in
 * one place." The mode-aware "GST + BC PST" / "GST only" detail this file
 * used to bake into each option's label (taxClassOptionLabel, removed here)
 * now lives ONLY in taxClassTooltipCopy()'s single ⓘ tooltip string, shared
 * by every surface that shows the "Tax class" control.
 */

/** The tax-class values the backend engine knows (services/tax_engine.py). */
export type TaxClass =
  | 'architectural'
  | 'pre_design'
  | 'post_construction'
  | 'administrative'
  | 'reimbursable'
  | 'pass_through';

export const TAX_CLASS_OPTIONS: ReadonlyArray<{ value: TaxClass; label: string }> = [
  { value: 'architectural', label: 'Architectural' },
  { value: 'pre_design', label: 'Pre-design' },
  { value: 'post_construction', label: 'Post-construction' },
  { value: 'administrative', label: 'Administrative' },
  { value: 'reimbursable', label: 'Reimbursable' },
  { value: 'pass_through', label: 'Pass-through' },
];

/** Sentinel for "no class chosen" in selects (NULL in the database). */
export const TAX_CLASS_UNSET = '__unset__';

/** Short human label for a class value; NULL/unset reads as "Unclassified". */
export function taxClassLabel(taxClass: string | null | undefined): string {
  if (!taxClass) return 'Unclassified';
  const found = TAX_CLASS_OPTIONS.find((o) => o.value === taxClass);
  return found ? found.label : taxClass;
}

/** The tax-rule `code` (schemas/tax_rules.py) that BC PST is stored under. */
const BC_PST_RULE_CODE = 'BC_PST';

/**
 * The subset of GET /api/me/settings/tax-engine's response a caller needs to
 * know whether BC PST is actually being charged right now. Optional/undefined
 * (settings still loading, or the fetch failed) is treated the same as
 * "not live" — the fail direction is always "don't promise a tax that may not
 * be owed" (docs/compliance/bc-pst-october-2026-runbook.md).
 */
export interface TaxEngineContext {
  tax_engine?: 'legacy' | 'rules' | null;
  rules?: ReadonlyArray<{ code: string; enabled: boolean; effective_from?: string | null }> | null;
  /** Compare effective_from against this date instead of "now" (tests only). */
  asOf?: Date;
}

/**
 * Whether BC PST is actually being charged on an invoice right now, given the
 * tenant's tax-engine settings. False whenever ANY of the following holds:
 * the tenant is on `legacy` (BC PST is inert there regardless of rule state —
 * legacy invoices carry only the line's own tax_rate); no BC_PST rule is
 * configured; the rule is `enabled: false`; or the rule's `effective_from` is
 * in the future relative to `asOf` (default: today). Comparing the ISO date
 * strings lexically is safe — `effective_from` is always YYYY-MM-DD.
 */
export function isBcPstLive(context: TaxEngineContext | null | undefined): boolean {
  if (!context || context.tax_engine !== 'rules') return false;
  const rule = context.rules?.find((r) => r.code === BC_PST_RULE_CODE);
  if (!rule || !rule.enabled) return false;
  if (!rule.effective_from) return true;
  const asOf = context.asOf ?? new Date();
  // The LOCAL calendar date -- never the ISO/UTC string form, which is the UTC date.
  // On the evening of Sep 30 in Pacific time UTC is already Oct 1, so the
  // UTC form would announce BC PST hours before it applies -- the same bug
  // class the v0.17 local-date fix removed everywhere else.
  const today = `${asOf.getFullYear()}-${String(asOf.getMonth() + 1).padStart(2, '0')}-${String(asOf.getDate()).padStart(2, '0')}`;
  return rule.effective_from <= today;
}

/**
 * v0.17 taxclass-light: the ONE place the PST detail lives now that every
 * dropdown option shows just the class name (QuickBooks/Xero convention --
 * short at the line, detail in one place). Rendered behind a single ⓘ
 * tooltip, shared by every "Tax class" control (InvoiceDetailPage's two
 * line-item table headers, ProjectCategoriesTab's default-class control) so
 * the wording can never drift between surfaces. Mode-aware via
 * isBcPstLive() with the same fail-closed direction as the rest of this
 * file: absent/loading/errored settings, legacy mode, a disabled rule, or a
 * not-yet-effective rule all read as "not live" -- never promise a tax that
 * may not be owed.
 */
export function taxClassTooltipCopy(context?: TaxEngineContext | null): string {
  if (isBcPstLive(context)) {
    return (
      'Sets which taxes apply to a line. Architectural and Administrative services attract ' +
      'BC PST (7% on 30% of the fee); other classes are GST only. Pass-through lines carry no tax.'
    );
  }
  return (
    'Sets which taxes apply to a line. Architectural and Administrative services will attract ' +
    'BC PST once it is enabled for your account; other classes are GST only.'
  );
}

/**
 * AC31: whether a line's class was set by hand rather than inherited from
 * its project's category default. True when the line carries a class that
 * DIFFERS from the category's current default — including a class on a line
 * whose category has no default (someone chose it manually) and a NULL
 * class on a line whose category would default one (someone cleared it).
 * Non-service lines (pass_through / billable_exp expense items) inherit
 * nothing by design, so they are never "overridden".
 */
export function isTaxClassOverridden(
  taxClass: string | null | undefined,
  categoryDefault: string | null | undefined,
  billableItemCategory?: string | null,
): boolean {
  const cls = taxClass ?? null;
  const dflt = categoryDefault ?? null;
  if (!dflt) return cls !== null;
  // Non-service items never inherit (backend _NON_SERVICE_CATEGORIES gate),
  // so a class on one is a hand choice only when it is non-null.
  if (
    billableItemCategory === 'pass_through' ||
    billableItemCategory === 'billable_expense' ||
    billableItemCategory === 'internal_work'
  ) {
    return cls !== null;
  }
  return cls !== dflt;
}
